/**
 * Admin Message Store — handles admin-to-user notification messages.
 *
 * Stores the latest admin message and tracks dismissed message IDs
 * in localStorage so they don't reappear after dismissal.
 */

import { create } from "zustand";
import { devtools } from "zustand/middleware";

// ─── Types ──────────────────────────────────────────────────────────────────

export interface AdminMessage {
  id: string;
  subject?: string;
  content: string;
  created_at: string;
}

interface AdminMessageStoreState {
  /** The currently visible admin message (null if none or dismissed). */
  activeMessage: AdminMessage | null;
}

interface AdminMessageStoreActions {
  /** Handle an incoming admin message from the socket. */
  handleMessageReceived: (message: AdminMessage) => void;
  /** Dismiss the active message and persist to localStorage. */
  dismiss: () => void;
}

type AdminMessageStore = AdminMessageStoreState & AdminMessageStoreActions;

// ─── localStorage helpers ────────────────────────────────────────────────────

const DISMISSED_KEY = "bash:admin-messages:dismissed";

function getDismissedIds(): Set<string> {
  try {
    const raw = localStorage.getItem(DISMISSED_KEY);
    if (!raw) return new Set();
    return new Set(JSON.parse(raw) as string[]);
  } catch {
    return new Set();
  }
}

function addDismissedId(id: string): void {
  try {
    const ids = getDismissedIds();
    ids.add(id);
    // Keep only last 100 dismissed IDs to avoid localStorage bloat
    const arr = Array.from(ids).slice(-100);
    localStorage.setItem(DISMISSED_KEY, JSON.stringify(arr));
  } catch {
    // localStorage unavailable — silently ignore
  }
}

// ─── Store ──────────────────────────────────────────────────────────────────

export const useAdminMessageStore = create<AdminMessageStore>()(
  devtools(
    (set, get) => ({
      activeMessage: null,

      handleMessageReceived: (message: AdminMessage) => {
        // Don't show if already dismissed
        if (getDismissedIds().has(message.id)) return;
        set({ activeMessage: message });
      },

      dismiss: () => {
        const { activeMessage } = get();
        if (activeMessage) {
          addDismissedId(activeMessage.id);
        }
        set({ activeMessage: null });
      },
    }),
    {
      name: "AdminMessageStore",
      enabled: process.env.NODE_ENV === "development",
    }
  )
);
